// Pages/almacenamiento/almacenamiento.tsx
//
// /almacenamiento — gestión VOLUNTARIA de la información pesada (fotos, videos
// y documentos) por administrador y superadministrador: cuánto pesa, de cuándo
// es, en qué módulo y dónde se usa, quién lo subió, y eliminarla sabiendo
// exactamente qué se elimina.
//
// Endpoints que consume (todos bajo /api/almacenamiento, solo admin; ver
// services/almacenamiento.api.ts):
//   GET  /resumen            salud del espacio (volumen, totales, módulos, tipos)
//   POST /refrescar          vuelve a escanear el volumen
//   GET  /archivos           lista paginada en el servidor con filtros y orden
//   POST /impacto            qué se llevaría eliminar una selección (solo lectura)
//   POST /eliminar           borrado definitivo (confirmación "ELIMINAR" obligatoria)
//   GET  /registro           memoria de lo eliminado (quién, cuándo, qué usaba)
//   GET  /consumo            datos entregados por /files (sección plegable «Consumo de datos»)
//   POST /previews/generar   rellena la versión liviana de los videos existentes (VersionesLivianas)
//   GET  /previews/estado    avance de ese relleno (sondeo cada ~5 s solo mientras corre)
//   (GET /propiedades        catálogo para el filtro por propiedad)
//
// Abrir un archivo (round 2026-10-28): clic en el nombre / la miniatura, botón «Abrir» o Enter en la
// fila. Imagen y video → VisorArchivoModal (el video con <VideoPlayer>); PDF → pestaña nueva;
// Office y demás → descarga. No hay endpoint nuevo: usa las URLs firmadas que ya trae el listado.
// Al pasar el mouse sobre una miniatura se amplía (solo la miniatura; ver ArchivoMiniatura).
//
// Solo administrador y superadministrador: la ruta lleva ProtectedRoute allowedRoles
// ["administrador"] en App.tsx y el menú la muestra solo a ese rol.
//
// Reglas del proyecto que aplican aquí: filtros con useFiltroPersistido (claves
// "almacenamiento.*"; la selección de filas y la página NO se persisten),
// nombres de propiedad en MAYÚSCULAS al mostrarlos, fechas en hora Chile.
import React, { useMemo, useState } from "react";
import { Alert, Button, Grid, Segmented, Space, Typography } from "antd";
import { FolderOpenOutlined, HistoryOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./almacenamiento.css";
import Layout from "../../Components/Layout";
import InfoAyuda, { AyudaBloque } from "../../Components/InfoAyuda";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { getMsg } from "../../utils/antdStatic";
import type { Archivo } from "../../services/almacenamiento.api";
import { resumenDelFiltro } from "./almacenamientoUtils";
import { abrirPdfEnPestana, aperturaDeArchivo, descargarArchivo } from "./aperturaArchivo";
import ArchivosLista from "./components/ArchivosLista";
import BarraSeleccion from "./components/BarraSeleccion";
import ConsumoDatos from "./components/ConsumoDatos";
import EliminarArchivosModal from "./components/EliminarArchivosModal";
import PanelFiltros from "./components/PanelFiltros";
import RegistroEliminados from "./components/RegistroEliminados";
import VisorArchivoModal from "./components/VisorArchivoModal";
import ResumenSalud from "./components/ResumenSalud";
import VersionesLivianas from "./components/VersionesLivianas";
import { useAlmacenamientoLogic, type VistaAlmacenamiento } from "./useAlmacenamientoLogic";

const { Text } = Typography;
const CLAVE_AYUDA_CERRADA = "almacenamiento.ayudaCerrada";

const leerAyudaCerrada = (): boolean => {
  try {
    return localStorage.getItem(CLAVE_AYUDA_CERRADA) === "1";
  } catch {
    return false;
  }
};

const AYUDA_CORTA =
  "Limpia con seguridad: 1) empieza por los huérfanos, que nadie usa; 2) revisa «Dónde se usa» y la vista previa antes de eliminar; 3) eliminar es irreversible (solo un backup lo recupera) y queda anotado en «Registro de eliminados».";

/**
 * Explicación COMPLETA de la página (el (i) del encabezado). Cada afirmación sale de
 * docs/modulo-almacenamiento.md y del código de los endpoints: si el comportamiento cambia,
 * hay que cambiar esto también.
 */
const AyudaAlmacenamiento: React.FC = () => (
  <InfoAyuda titulo="Cómo funciona Almacenamiento" texto="¿Cómo funciona?" testId="ayuda-almacenamiento">
    <AyudaBloque titulo="Qué es">
      Aquí el administrador ve y limpia lo que más espacio ocupa: las fotos, los videos y los
      documentos guardados en el volumen del servidor. Nada se borra solo: cada eliminación la
      decide una persona. Los clientes, las aseadoras y los demás usuarios no pueden entrar a esta
      pantalla; solo ven sus archivos dentro de cada módulo.
    </AyudaBloque>

    <AyudaBloque titulo="La barra del volumen y por qué importa">
      Muestra cuánto del disco del servidor está ocupado. Todo lo que suben los usuarios vive en
      ese único volumen y además engorda los respaldos. Verde hasta 70 %; desde 70 % conviene ir
      limpiando; desde 85 % el espacio es escaso; desde 95 % es crítico. Por defecto, el sistema
      además avisa en la campana de alertas al llegar al 85 % y al 95 %, y el aviso se resuelve
      solo al bajar de 80 %.
    </AyudaBloque>

    <AyudaBloque titulo="El peso de cada archivo">
      Se lee del disco (no del registro de la subida) y suma el archivo, su miniatura y, en los
      videos, su <b>versión liviana</b>. Al abrir el detalle de una fila ves el desglose.
    </AyudaBloque>

    <AyudaBloque titulo="Por qué los números no siempre coinciden">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          <b>Archivos de esta lista</b>: cada fila es un archivo que una persona reconoce. Una foto
          con su miniatura, o un video con su miniatura y su versión liviana, cuenta como{" "}
          <b>uno solo</b>, y su peso suma las tres piezas.
        </li>
        <li>
          <b>Backup BD</b> (superadministrador): copia <b>archivos físicos</b>, así que cuenta el
          original, la miniatura y la versión liviana por separado. Por eso 11 archivos aquí pueden
          ser 24 en el backup («11 originales + 10 miniaturas + 3 versiones livianas»). El peso, en
          cambio, coincide (salvo lo que haya suelto en el volumen fuera de las carpetas conocidas,
          que el backup cuenta como «otros»).
        </li>
        <li>
          <b>Volumen «usados de»</b>: es el disco entero, con todo lo que haya en él. La suma de la
          lista solo cuenta las fotos, videos y documentos, así que el volumen puede marcar un poco
          más (temporales de un video que se está optimizando, por ejemplo).
        </li>
        <li>
          <b>«Liberarías» / «se liberan»</b>: suma el archivo, su miniatura y su versión liviana,
          que se borran juntos. Es lo que de verdad queda libre, salvo los archivos que se omiten
          (recién subidos o en optimización), que no se cuentan.
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Estados">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          <b>En uso</b>: algún registro del sistema usa el archivo (el detalle dice dónde y si lo
          ve el cliente).
        </li>
        <li>
          <b>Huérfano</b>: ocupa espacio pero nada lo usa (por ejemplo, lo que quedó al borrar un
          aseo, una tarea o un gasto). Eliminarlo no afecta a nadie. Una versión liviana cuyo
          video ya no existe aparece como «Versión liviana sin video».
        </li>
        <li>
          <b>Referencia rota</b>: un registro apunta a un archivo que ya no existe en el disco.
          Eliminar la referencia limpia el registro.
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Atajos de limpieza">
      Huérfanos, más de 6 meses, más de 1 año, videos pesados (más de 100 MB) y lo más pesado.
      Cada atajo reemplaza los filtros que tengas puestos, y tus filtros se recuerdan la próxima
      vez.
    </AyudaBloque>

    <AyudaBloque titulo="Ver y abrir un archivo">
      Al pasar el mouse sobre una miniatura se agranda (usa la miniatura, no baja el original; en
      el celular no aparece). Al hacer clic en el nombre, en la miniatura o en el botón del ojo se
      abre: las imágenes y los videos en un visor aquí mismo (el video parte en la versión liviana
      y puedes pasar a HD), los PDF en una pestaña nueva y los demás documentos se descargan. Abrir
      cuenta como una lectura normal del archivo en el consumo de datos. Si la referencia está rota
      no hay nada que abrir.
    </AyudaBloque>

    <AyudaBloque titulo="Cómo se elimina (en dos pasos)">
      Eliges las filas (o todo el filtro, hasta 500 por vez). Primero el sistema calcula el
      impacto: qué módulos y registros se verían afectados y si algún cliente lo ve. Después tienes
      que escribir <b>ELIMINAR</b> para confirmar. Es <b>irreversible</b>: solo un respaldo que
      incluya los medios (Backup BD, superadministrador) puede recuperarlo.
      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        <li>
          Protege lo recién subido (menos de 5 minutos) y los videos que se están optimizando: se
          omiten y se explica por qué.
        </li>
        <li>Borrar un video se lleva también su miniatura y su versión liviana.</li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Registro de eliminados">
      La otra pestaña guarda quién eliminó cada archivo, cuándo, cuánto pesaba y qué usaba. Es la
      memoria de lo que ya no está.
    </AyudaBloque>

    <AyudaBloque titulo="Consumo de datos">
      La tarjeta plegable «Consumo de datos» muestra cuánto entregó el servidor por /files en los
      últimos 7 o 30 días (por tipo, por día y los archivos más servidos). Solo mide fotos,
      videos y documentos: no incluye la API ni las subidas.
    </AyudaBloque>

    <AyudaBloque titulo="Versiones livianas de los videos">
      Cada video puede tener una copia liviana (480p por defecto) que el reproductor usa para
      gastar menos datos. Los videos nuevos la reciben solos después de optimizarse. Para los que
      ya existían aparece la tarjeta «videos sin versión liviana» con el botón «Generar versiones
      livianas»: trabaja en segundo plano, de a un video, sin tocar los originales, y puedes seguir
      el avance aquí. Si la función está apagada en Configuración → Optimización de videos, la
      tarjeta no aparece.
    </AyudaBloque>

    <AyudaBloque titulo="Si el volumen se llena">
      Primero limpia (huérfanos y videos pesados suelen ser lo más grande). Si no alcanza, el
      volumen se puede ampliar en Railway, en el servicio del backend (Volumes): se amplía en vivo
      y no se puede reducir después. Hazlo antes de llegar al 100 %: con el volumen lleno no se
      pueden subir archivos y Railway lo amplía por su cuenta fuera de línea, reiniciando el
      servicio. Después pulsa «Reescanear»: la barra de esta pantalla confirma la capacidad nueva.
    </AyudaBloque>

    <AyudaBloque titulo="Qué NO hace">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>No comprime ni recorta archivos existentes: solo los elimina, si tú lo decides.</li>
        <li>No recupera lo eliminado.</li>
        <li>
          Los archivos que viven en un servicio externo antiguo se cuentan como «Externos»: no
          ocupan este disco y no se pueden eliminar desde aquí.
        </li>
      </ul>
    </AyudaBloque>
  </InfoAyuda>
);

const Almacenamiento: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  // Hasta que AntD mide el ancho `md` viene `undefined`: se asume escritorio.
  const esMovil = screens.md === false;

  const L = useAlmacenamientoLogic();
  const [aEliminar, setAEliminar] = useState<Archivo[] | null>(null);
  /** Archivo abierto en el visor (imagen o video). Mientras sea null no se monta nada. */
  const [visor, setVisor] = useState<Archivo | null>(null);
  const [ayudaCerrada, setAyudaCerrada] = useState<boolean>(leerAyudaCerrada);

  const opcionesModulo = useMemo(
    () =>
      (L.resumen?.porModulo ?? [])
        .map((m) => ({ value: m.modulo, label: m.etiqueta }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [L.resumen],
  );

  const resumenFiltro = useMemo(
    () => resumenDelFiltro(L.total, L.bytesFiltrados, L.resumen?.archivos.bytes ?? 0),
    [L.total, L.bytesFiltrados, L.resumen],
  );

  const cerrarAyuda = () => {
    setAyudaCerrada(true);
    try {
      localStorage.setItem(CLAVE_AYUDA_CERRADA, "1");
    } catch {
      /* sin storage: solo se recuerda mientras dure la página */
    }
  };

  /**
   * Abre el ARCHIVO según su tipo: imagen y video en el visor de la página, PDF en una pestaña
   * nueva, Office y demás se descargan (la URL de /files exige la sesión por cookie, así que no hay
   * visores de terceros). Una referencia rota o sin enlace no se abre.
   */
  const abrirArchivo = (a: Archivo) => {
    const ap = aperturaDeArchivo(a);
    switch (ap.modo) {
      case "imagen":
      case "video":
        setVisor(a);
        break;
      case "pdf":
        abrirPdfEnPestana(a.url as string);
        break;
      case "descarga":
        // El aviso de éxito solo si la descarga arrancó: con un enlace vencido o un archivo borrado
        // `descargarArchivo` ya avisó el motivo y no se navega.
        void descargarArchivo(a.url as string, a.nombre).then((ok) => {
          if (ok) getMsg()?.info("Este formato no se puede ver aquí: se descargó el archivo.");
        });
        break;
      default:
        if (ap.motivo) getMsg()?.warning(ap.motivo);
    }
  };

  const alCerrarModal = (huboCambios: boolean) => {
    setAEliminar(null);
    if (huboCambios) void L.trasEliminar();
  };

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="alm-page"
    >
      <div className="alm-contenedor">
        <PageHeader
          title="Almacenamiento"
          extra={
            <Space wrap>
              {/* PageHeader solo acepta texto como título: el (i) va junto a los controles. */}
              <AyudaAlmacenamiento />
              <Segmented<VistaAlmacenamiento>
                value={L.vista}
                onChange={(v) => L.setVista(v)}
                options={[
                  { value: "archivos", label: "Archivos", icon: <FolderOpenOutlined /> },
                  {
                    value: "registro",
                    label: "Registro de eliminados",
                    icon: <HistoryOutlined />,
                  },
                ]}
              />
            </Space>
          }
        />

        {!ayudaCerrada && (
          <Alert
            className="alm-ayuda"
            type="info"
            showIcon
            closable
            onClose={cerrarAyuda}
            message="Cómo limpiar con seguridad"
            description={AYUDA_CORTA}
          />
        )}

        {L.vista === "archivos" ? (
          <>
            <ResumenSalud
              resumen={L.resumen}
              cargando={L.cargandoResumen}
              refrescando={L.refrescando}
              error={L.errorResumen}
              onReescanear={() => void L.cargarResumen(true)}
              onReintentar={() => void L.cargarResumen(false)}
              moduloActivo={L.filtros.modulo}
              tipoActivo={L.filtros.tipo}
              onFiltrarModulo={L.setModulo}
              onFiltrarTipo={L.setTipo}
              onVerHuerfanos={() => L.aplicarAtajo("huerfanos")}
              onVerRotos={() => L.setEstado("roto")}
            />

            <VersionesLivianas
              sinVersion={L.resumen?.videosSinVersionLiviana}
              onTerminado={() => void L.trasGenerarPreviews()}
            />

            <ConsumoDatos />

            <PanelFiltros
              filtros={L.filtros}
              hayFiltros={L.filtrosActivos}
              textoBusqueda={L.textoBusqueda}
              onTextoBusqueda={L.setTextoBusqueda}
              onBuscar={L.setBusqueda}
              modulos={opcionesModulo}
              propiedades={L.propiedades}
              rango={L.rango}
              onRango={L.setRango}
              onModulo={L.setModulo}
              onTipo={L.setTipo}
              onPropiedad={L.setPropiedadId}
              onAntiguedad={L.setAntiguedadMeses}
              onPesoMin={L.setPesoMinMB}
              onEstado={L.setEstado}
              onOrden={L.setOrden}
              onDir={L.setDir}
              onAtajo={L.aplicarAtajo}
              onLimpiar={L.limpiarFiltros}
            />

            {L.errorLista ? (
              <Alert
                type="error"
                showIcon
                message="No se pudo cargar la lista de archivos"
                description="Falló la consulta. No se muestran datos para no inducir a error."
                action={
                  <Button
                    className="alm-btn-tactil"
                    loading={L.cargandoLista}
                    onClick={() => void L.cargarLista(1, L.size)}
                  >
                    Reintentar
                  </Button>
                }
              />
            ) : (
              <>
                <div className="alm-resultado" aria-live="polite">
                  <Text strong>{resumenFiltro.texto}</Text>
                  {L.resumen && L.resumen.archivos.bytes > 0 && (
                    <Text type="secondary">
                      {" "}
                      · {resumenFiltro.porcentajeTexto} del espacio de los archivos
                    </Text>
                  )}
                </div>

                <ArchivosLista
                  archivos={L.archivos}
                  cargando={L.cargandoLista}
                  esMovil={esMovil}
                  page={L.page}
                  size={L.size}
                  total={L.total}
                  onPaginar={L.cambiarPagina}
                  seleccion={L.seleccion}
                  onAlternar={L.alternarFilas}
                  orden={L.filtros.orden}
                  dir={L.filtros.dir}
                  onOrdenar={(o, d) => {
                    L.setOrden(o);
                    L.setDir(d);
                  }}
                  onEliminarUno={(a) => setAEliminar([a])}
                  onAbrir={(ruta) => navigate(ruta)}
                  onAbrirArchivo={abrirArchivo}
                />
              </>
            )}

            <BarraSeleccion
              cantidad={L.totalSel.cantidad}
              bytes={L.totalSel.bytes}
              totalFiltro={L.total}
              seleccionandoTodo={L.seleccionandoTodo}
              onSeleccionarFiltro={() => void L.seleccionarTodoElFiltro()}
              onLimpiar={L.limpiarSeleccion}
              onEliminar={() => setAEliminar(Array.from(L.seleccion.values()))}
            />
          </>
        ) : (
          <RegistroEliminados esMovil={esMovil} />
        )}
      </div>

      {visor && <VisorArchivoModal archivo={visor} onClose={() => setVisor(null)} />}

      <EliminarArchivosModal
        open={aEliminar !== null}
        archivos={aEliminar ?? []}
        onClose={alCerrarModal}
      />
    </Layout>
  );
};

export default Almacenamiento;
